import { openDB } from "idb";
import type { ActiveSession } from "../shared/types";
import { reconcileWorkoutPointer } from "../shared/engine";

export interface QueuedAction {
  id: string;
  profileId: string;
  path: string;
  method: "POST" | "PATCH" | "DELETE";
  body?: unknown;
  createdAt: string;
  sequence?: number;
  /** Unique to this exact value, even when a coalesced action reuses `id`. */
  mutationToken?: string;
  attempts?: number;
  lastError?: string;
}

export interface FlushResult {
  remaining: number;
  blocked: QueuedAction | null;
  responses: Record<string, unknown>;
}

const profileFlushes = new Map<string, Promise<FlushResult>>();

const dbPromise = openDB("hardnup-offline", 4, {
  upgrade(db, oldVersion, _newVersion, transaction) {
    if (oldVersion < 1) {
      db.createObjectStore("state");
      const queue = db.createObjectStore("queue", { keyPath: "id" });
      queue.createIndex("createdAt", "createdAt");
    } else if (oldVersion < 2) {
      transaction.objectStore("queue").createIndex("createdAt", "createdAt");
    }
    if (oldVersion < 4) {
      transaction.objectStore("state").clear();
      transaction.objectStore("queue").clear();
    }
    if (oldVersion < 3) {
      const queue = transaction.objectStore("queue");
      if (!queue.indexNames.contains("profileId"))
        queue.createIndex("profileId", "profileId");
      // Version 1/2 records had no owner. They are unsafe to replay under any signed-in cookie.
      // Quarantine by clearing them rather than risking Craig/Cazzy cross-profile writes.
      queue.clear();
      transaction.objectStore("state").delete("active");
    }
  },
});

const activeKey = (profileId: string) => `active:${profileId}`;

function versioned(action: QueuedAction): QueuedAction {
  return action.mutationToken
    ? action
    : { ...action, mutationToken: crypto.randomUUID() };
}

function isSameMutation(
  current: QueuedAction | undefined,
  snapshot: QueuedAction,
) {
  if (!current) return false;
  if (current.mutationToken || snapshot.mutationToken)
    return current.mutationToken === snapshot.mutationToken;
  // Compatibility for a queue record written by the pre-token release.
  return (
    current.profileId === snapshot.profileId &&
    current.createdAt === snapshot.createdAt &&
    current.sequence === snapshot.sequence &&
    current.path === snapshot.path &&
    current.method === snapshot.method &&
    JSON.stringify(current.body) === JSON.stringify(snapshot.body)
  );
}

export async function saveActiveSession(
  profileId: string,
  session: ActiveSession | null,
) {
  const db = await dbPromise;
  if (session) await db.put("state", session, activeKey(profileId));
  else await db.delete("state", activeKey(profileId));
}

export async function getActiveSession(
  profileId: string,
): Promise<ActiveSession | null> {
  const db = await dbPromise;
  const session =
    ((await db.get("state", activeKey(profileId))) as
      | ActiveSession
      | undefined) ?? null;
  if (!session) return null;
  const pointer = reconcileWorkoutPointer(
    session.template,
    session.sets.map((set) => ({
      templateExerciseId: set.templateExerciseId,
      setNumber: set.setNumber,
    })),
    session.pointer,
  );
  if (
    pointer.groupIndex !== session.pointer.groupIndex ||
    pointer.position !== session.pointer.position ||
    pointer.round !== session.pointer.round ||
    pointer.restEndAt !== session.pointer.restEndAt ||
    pointer.selectionRequired !== session.pointer.selectionRequired
  ) {
    const repaired = { ...session, pointer };
    await db.put("state", repaired, activeKey(profileId));
    return repaired;
  }
  return session;
}

export async function enqueue(action: QueuedAction) {
  if (!action.profileId)
    throw new Error("Offline actions require an owning profile.");
  await (await dbPromise).put("queue", versioned(action));
}

export async function persistWorkoutTransition(
  profileId: string,
  session: ActiveSession,
  actions: QueuedAction[],
) {
  if (actions.some((action) => action.profileId !== profileId))
    throw new Error("Offline transition contains a different profile owner.");
  const db = await dbPromise;
  const transaction = db.transaction(["state", "queue"], "readwrite");
  await transaction.objectStore("state").put(session, activeKey(profileId));
  for (const action of actions)
    await transaction.objectStore("queue").put(versioned(action));
  await transaction.done;
}

export async function persistUndoTransition(
  profileId: string,
  session: ActiveSession,
  removeIds: string[],
  tombstone: QueuedAction,
) {
  if (tombstone.profileId !== profileId)
    throw new Error("Undo tombstone contains a different profile owner.");
  const db = await dbPromise;
  const transaction = db.transaction(["state", "queue"], "readwrite");
  await transaction.objectStore("state").put(session, activeKey(profileId));
  for (const id of removeIds) {
    const action = (await transaction.objectStore("queue").get(id)) as
      QueuedAction | undefined;
    if (!action || action.profileId === profileId)
      await transaction.objectStore("queue").delete(id);
  }
  await transaction.objectStore("queue").put(versioned(tombstone));
  await transaction.done;
}

export async function removeQueued(profileId: string, id: string) {
  const db = await dbPromise;
  const action = (await db.get("queue", id)) as QueuedAction | undefined;
  if (action?.profileId === profileId) await db.delete("queue", id);
}

export async function queuedCount(profileId: string) {
  return (
    await (await dbPromise).getAllFromIndex("queue", "profileId", profileId)
  ).length;
}

async function flushProfileQueue(profileId: string): Promise<FlushResult> {
  if (!navigator.onLine)
    return {
      remaining: await queuedCount(profileId),
      blocked: null,
      responses: {},
    };
  const db = await dbPromise;
  let blocked: QueuedAction | null = null;
  const responses: Record<string, unknown> = {};
  while (navigator.onLine) {
    const actions = (
      (await db.getAllFromIndex(
        "queue",
        "profileId",
        profileId,
      )) as QueuedAction[]
    ).sort(
      (left, right) =>
        left.createdAt.localeCompare(right.createdAt) ||
        (left.sequence ?? 0) - (right.sequence ?? 0) ||
        left.id.localeCompare(right.id),
    );
    if (!actions.length) break;
    let restartFromCurrentQueue = false;
    for (const action of actions) {
      try {
        const response = await fetch(`/api${action.path}`, {
          method: action.method,
          credentials: "same-origin",
          headers: {
            "X-Hardnup-Profile": profileId,
            ...(action.body ? { "Content-Type": "application/json" } : {}),
          },
          body: action.body ? JSON.stringify(action.body) : undefined,
        });
        if (response.ok) {
          const text = await response.text().catch(() => "");
          let parsed: unknown;
          if (text) {
            try {
              parsed = JSON.parse(text) as unknown;
            } catch {
              parsed = text;
            }
          }
          const transaction = db.transaction(["queue", "state"], "readwrite");
          const queue = transaction.objectStore("queue");
          const current = (await queue.get(action.id)) as
            | QueuedAction
            | undefined;
          const acknowledged = isSameMutation(current, action);
          if (acknowledged) {
            await queue.delete(action.id);
            if (
              /\/(?:sessions\/[^/]+\/(?:complete|skip-advance)|records\/[^/]+\/(?:stop|close))$/.test(action.path)
            ) {
              const cached = await transaction.objectStore("state").get(activeKey(profileId)) as ActiveSession | undefined;
              if (cached && action.path.includes(`/${cached.id}/`))
                await transaction.objectStore("state").delete(activeKey(profileId));
            }
          }
          await transaction.done;
          if (acknowledged && text) responses[action.id] = parsed;
          if (!acknowledged) {
            // A coalesced action with the same key was written while fetch was
            // in flight. Do not let the stale response delete it; resnapshot so
            // the newer value is sent before any later mutation.
            restartFromCurrentQueue = true;
            break;
          }
          continue;
        }
        const details = await response.text().catch(() => "");
        const failed = {
          ...action,
          attempts: (action.attempts ?? 0) + 1,
          lastError: `${response.status} ${details}`.slice(0, 500),
        };
        const transaction = db.transaction("queue", "readwrite");
        const queue = transaction.objectStore("queue");
        const current = (await queue.get(action.id)) as
          | QueuedAction
          | undefined;
        if (isSameMutation(current, action)) {
          await queue.put(failed);
          blocked = failed;
        } else {
          restartFromCurrentQueue = true;
        }
        await transaction.done;
        break;
      } catch (caught) {
        const failed = {
          ...action,
          attempts: (action.attempts ?? 0) + 1,
          lastError:
            caught instanceof Error ? caught.message : "Network error",
        };
        const transaction = db.transaction("queue", "readwrite");
        const queue = transaction.objectStore("queue");
        const current = (await queue.get(action.id)) as
          | QueuedAction
          | undefined;
        if (isSameMutation(current, action)) {
          await queue.put(failed);
          blocked = failed;
        } else {
          restartFromCurrentQueue = true;
        }
        await transaction.done;
        break;
      }
    }
    if (blocked) break;
    if (restartFromCurrentQueue) continue;
  }
  return { remaining: await queuedCount(profileId), blocked, responses };
}

/**
 * A profile has one ordered writer. This prevents an eager online flush, an
 * `online` event, and a Finish click from racing the same set/pointer stream.
 */
export function flushQueue(profileId: string): Promise<FlushResult> {
  const previous =
    profileFlushes.get(profileId) ??
    Promise.resolve({ remaining: 0, blocked: null, responses: {} });
  const current = previous
    .catch(() => ({ remaining: 0, blocked: null, responses: {} }))
    .then(() => flushProfileQueue(profileId));
  profileFlushes.set(profileId, current);
  void current.finally(() => {
    if (profileFlushes.get(profileId) === current)
      profileFlushes.delete(profileId);
  });
  return current;
}

export function getDeviceId(): string {
  const key = "hardnup-device-id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}
